import styled from '@emotion/styled';
import React, { FC, useState } from 'react';

import PredefinedMessages, { Message } from '~Components/PredefinedMessages';
import Modal from '~ComponentsImproved/Modal';
import {
  useButton,
  useDispatch,
  useI18n,
  useKitchenMessage,
  useLogger,
  useSelector,
} from '~Hooks';
import { BaseButton, RemoveButton } from '~Scenes/Handy/styles';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { addToast } from '~actions';
import { Box } from '~Styles';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';

const Button = styled(BaseButton)`
  background: var(--colorBlueGray50);
  color: var(--colorBlueGray1300);
`;

const MessagesCancelButton = styled(RemoveButton)`
  width: 100px;
  align-self: flex-start;
  margin-left: 0;
`;

interface Props {
  messagesList: (Message | string)[];
}

/**
 *
 * This component is used to show predefined messages(type CHEQUE) and course types and clicking on them will send a message to the kitchen
 * If a course type is clicked the cheque will be progressed to that course
 * @param messagesList The list of combined predefined messages and course types but can also contain other strings
 */
const ChequeMessageButton: FC<Props> = ({ messagesList }) => {
  const { i18n } = useI18n();
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const universe = useTableUniverse();
  const api = useTableApi();
  const sendKitchenMessage = useKitchenMessage();
  const dispatch = useDispatch();
  const logger = useLogger('cheque-message');
  const { selectedTable } = useTableContext();
  const venueId = useSelector((state) => state.venueId);
  const orderStatusApi = useOrderStatusApi(venueId || '');
  const orderStatusUniverse = useOrderStatusUniverse();

  const chequesForTable = universe.cheques.filter((c) => c.tableId === selectedTable?.id);
  const itemsOnCheques = universe.items.filter((i) =>
    chequesForTable.map((c) => c.id).includes(i.chequeId),
  );

  const nextButton = useButton({
    onClick: () => setShowMessageModal(true),
  });

  const handleSendMessage = (value: Message | string) => {
    const strToSend =
      typeof value === 'string' ? value : `${i18n('TSR.NextCourse.Short')} ${value.label}`;

    let items = itemsOnCheques;

    // If we've selected a course type, move the cheque to that course and filter items for that course type
    if (typeof value !== 'string') {
      // We use the first cheque because the kitchen only cares about the table and not a specific cheques
      api.progressCheque(chequesForTable[0].id, value.label);
      api.addMessage(strToSend, chequesForTable[0].id);
      items = items.filter((i) => i.courseId === value.id);

      // Fire all KDS orders for this table + course. The event carries the
      // criteria and every device resolves matching orders in the reducer, so
      // this works even when this device's local universe is missing the order
      // (previously this filtered the local universe and silently fired nothing).
      const courseId = value.id;
      const tableId = selectedTable?.id;
      logger.info('Firing KDS orders for table and course', { tableId, courseId });
      if (tableId) {
        orderStatusApi.fireCourse(courseId, { tableId, tableName: selectedTable?.code });
      }
    }

    sendKitchenMessage({
      tableName: selectedTable?.code ?? '',
      message: strToSend,
      items: items.map((item) => ({ itemId: item.itemId, categoryId: item.categoryId })),
    });

    dispatch(addToast(i18n('TOAST.Message.Sent'), true));

    setShowMessageModal(false);
  };

  if (messagesList.length === 0) return null;

  // We use the first cheque because the kitchen only cares about the table and not a specific cheques
  const firstCheque = chequesForTable[0];
  const chequeMessages = universe.messages.filter((m) => m.chequeId === firstCheque.id);
  const disabledMessages = messagesList.reduce((acc: string[], message) => {
    if (typeof message === 'string') {
      return acc;
    }

    // If the message equals the current stage, disable it
    if (firstCheque?.stage === message.label) {
      return [...acc, message.label];
    }

    // Remove the word "Next/Framma" from the message to know it is a course
    const formattedChequeMessages = chequeMessages.map((m) =>
      m.message.replace(i18n('TSR.NextCourse.Short'), '').trim(),
    );

    // If the course has already been sent to the kitchen, disable it
    if (formattedChequeMessages.includes(message.label)) {
      return [...acc, message.label];
    }

    return acc;
  }, []);

  return (
    <>
      <Button {...nextButton}>{i18n('Handy.Next.Course')}</Button>
      {showMessageModal && (
        <Modal
          isShowing={showMessageModal}
          hide={() => setShowMessageModal(false)}
          backdropClose={true}>
          <PredefinedMessages
            onSubmit={handleSendMessage}
            messages={messagesList ?? []}
            hideTextarea
            disabledMessages={disabledMessages}
          />
          <Box display="flex" justifyContent="center">
            <MessagesCancelButton onTouchEnd={() => setShowMessageModal(false)}>
              {i18n('General.Cancel')}
            </MessagesCancelButton>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default React.memo(ChequeMessageButton);
